import React from "react";
import { Prompt } from "react-router-dom";
import {Loader, Segment} from "semantic-ui-react";
import { Trans } from "react-i18next";
import { S3UploadButton } from "uploadButton";
import PropTypes from "prop-types";

import { Message } from '@weezevent/nacre';

import ImportRequest from "./model";

import ButtonGroup from "./stateless/buttonGroup";
import ShowAnalysis from "./stateless/analysis";
import ColumnMapping from "./stateless/columnMapping";
import ConfirmModal from "stateless/modals/confirmModal";

export default function ImportRender({
    step,
    titles,
    subtitles,
    abortWarning,
    onFileSelected,
    onFileUploaded,
    onReset,
    onTrigger,
    onSave,
    importRequest,
    authorizationURL,
    fetch,
    validationError,
    openModal=false,
    setOpenModal,
    children,
    options,
}) {
    const [showDetails, setDetails ] = React.useState(false);
    const toggleDetails = React.useCallback(() => setDetails(!showDetails), [showDetails]);
    const stepTitle = (titles && titles[step]) || step;
    const subtitle = (subtitles && subtitles[step]) || step;
    const [needSaveDetails, setNeedSaveDetails] = React.useState(false);

    return (
        <div className="import-controller">
            { (step === 'import-process-processing' && abortWarning)
                ?  <Prompt message={ abortWarning } />
                : null }
            <Segment>
                <Segment basic>
                    <h4>{ stepTitle }</h4>
                    { importRequest ?  <h5>{ importRequest.getFilenameWithExtension() }</h5> : null }
                    <div>{ subtitle }</div>
                    { (step === 'import-process-processing' || step === 'import-process-validating') &&
                        <Loader active inline='centered' />
                    }
                </Segment>

                { (step === 'initial' || step === 'file-uploading') &&
                    <div className="container-button">
                        <S3UploadButton
                            fetch={ fetch }
                            authorizationURL={ authorizationURL }
                            onFileUploaded={ onFileUploaded }
                            onFileSelected={ onFileSelected }
                            acceptedFileExtensions={ [".xlsx"] }
                        />
                    </div> }
                { (step === 'import-process-confirm' || step === 'import-process-done') &&
                    <ShowAnalysis
                        importRequest={ importRequest }
                        validationError={ validationError }
                        showDetails={ showDetails }
                        toggleDetails={ toggleDetails }
                    />
                }
                { step === 'import-process-confirm' &&
                    <ColumnMapping
                        options={ options }
                        importRequest={ importRequest }
                        onSubmit={ onSave }
                        setNeedSaveDetails={ setNeedSaveDetails }
                    />
                }
                { children }
                { step === 'create-import-process-waiting' &&
                    <ButtonGroup onReset={ onReset } /> }
                { step === 'import-process-confirm' &&
                    <ButtonGroup onReset={ onReset } onTrigger={ needSaveDetails ? null : onTrigger } /> }
            </Segment>
            {<ConfirmModal open={ openModal } validateLabel={ 'OK' } onValidate={ () => setOpenModal(false) }           >
                <Message
                    
                    type="danger"	
                    title={ <Trans i18nKey={ 'contacts.imports.error.title' }>File name error</Trans> }>
                    <Trans i18nKey='contacts.imports.error.filename'>File name is too long (90 chars max.)</Trans>  
                </Message>
            </ConfirmModal>}

        </div>
    );
}

ImportRender.propTypes = {
    step: PropTypes.oneOf([
        'initial',
        'file-uploading',
        'create-import-process-waiting',
        'import-process-validating',
        'import-process-confirm',
        'import-process-processing',
        'import-process-done',
    ]).isRequired,
    children: PropTypes.node,
    titles: PropTypes.object,
    subtitles: PropTypes.object,
    abortWarning: PropTypes.string,
    onFileSelected: PropTypes.func.isRequired,
    onFileUploaded: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    fetch: PropTypes.func,
    onTrigger: PropTypes.func,
    importRequest: PropTypes.instanceOf(ImportRequest),
    validationError: PropTypes.elementType,
    setOpenModal: PropTypes.func,
    openModal: PropTypes.bool,
    authorizationURL: PropTypes.string.isRequired,
    options: PropTypes.array
};
